import { z } from 'zod';
import { baseMessageSchema } from './_base';

export enum HostMessageType {
  HOST_SETTINGS = 'HOST_SETTINGS',
  HOST_SETTINGS_UPDATE = 'HOST_SETTINGS_UPDATE',
  HOST_SETTINGS_SUCCESS = 'HOST_SETTINGS_SUCCESS',
  HOST_SETTINGS_ERROR = 'HOST_SETTINGS_ERROR',
  HOST_ASSOCIATE_USER = 'HOST_ASSOCIATE_USER',
  HOST_ASSOCIATE_USER_SUCCESS = 'HOST_ASSOCIATE_USER_SUCCESS',
  HOST_ASSOCIATE_USER_ERROR = 'HOST_ASSOCIATE_USER_ERROR',
  HOST_UNASSOCIATE_USER = 'HOST_UNASSOCIATE_USER',
  HOST_UNASSOCIATE_USER_SUCCESS = 'HOST_UNASSOCIATE_USER_SUCCESS',
  HOST_UNASSOCIATE_USER_ERROR = 'HOST_UNASSOCIATE_USER_ERROR',
}

export type HostSettings = z.infer<typeof hostSettingsPayloadSchema>;

// User profile schema for future use
const userProfileSchema = z.object({
  username: z.string().optional(),
  email: z.string().email().optional(),
  profileId: z.string().optional(),
});

// Payload schemas
const hostSettingsPayloadSchema = z.object({
  displayName: z.string().optional(),
  maxPlayerCount: z.number().optional(),
  version: z.string().optional(),
});

const hostSettingsUpdatePayloadSchema = z.object({
  machineId: z.string(),
  settings: hostSettingsPayloadSchema,
});

const hostSettingsSuccessPayloadSchema = z.object({
  acknowledged: z.boolean(),
});

const hostSettingsErrorPayloadSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});

const hostAssociateUserPayloadSchema = z.object({
  machineId: z.string(),
  userProfile: userProfileSchema,
});

const hostAssociateUserSuccessPayloadSchema = z.object({
  acknowledged: z.boolean(),
});

const hostAssociateUserErrorPayloadSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});

const hostUnassociateUserPayloadSchema = z.object({
  machineId: z.string(),
  userProfile: userProfileSchema,
});

const hostUnassociateUserSuccessPayloadSchema = z.object({
  acknowledged: z.boolean(),
});

const hostUnassociateUserErrorPayloadSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});

// Full message schemas
const hostSettingsSchema = baseMessageSchema
  .extend({
    type: z.literal(HostMessageType.HOST_SETTINGS),
  })
  .merge(hostSettingsPayloadSchema);

const hostSettingsUpdateSchema = baseMessageSchema
  .extend({
    type: z.literal(HostMessageType.HOST_SETTINGS_UPDATE),
  })
  .merge(hostSettingsUpdatePayloadSchema);

const hostSettingsSuccessSchema = baseMessageSchema
  .extend({
    type: z.literal(HostMessageType.HOST_SETTINGS_SUCCESS),
  })
  .merge(hostSettingsSuccessPayloadSchema);

const hostSettingsErrorSchema = baseMessageSchema
  .extend({
    type: z.literal(HostMessageType.HOST_SETTINGS_ERROR),
  })
  .merge(hostSettingsErrorPayloadSchema);

const hostAssociateUserSchema = baseMessageSchema
  .extend({
    type: z.literal(HostMessageType.HOST_ASSOCIATE_USER),
  })
  .merge(hostAssociateUserPayloadSchema);

const hostAssociateUserSuccessSchema = baseMessageSchema
  .extend({
    type: z.literal(HostMessageType.HOST_ASSOCIATE_USER_SUCCESS),
  })
  .merge(hostAssociateUserSuccessPayloadSchema);

const hostAssociateUserErrorSchema = baseMessageSchema
  .extend({
    type: z.literal(HostMessageType.HOST_ASSOCIATE_USER_ERROR),
  })
  .merge(hostAssociateUserErrorPayloadSchema);

const hostUnassociateUserSchema = baseMessageSchema
  .extend({
    type: z.literal(HostMessageType.HOST_UNASSOCIATE_USER),
  })
  .merge(hostUnassociateUserPayloadSchema);

const hostUnassociateUserSuccessSchema = baseMessageSchema
  .extend({
    type: z.literal(HostMessageType.HOST_UNASSOCIATE_USER_SUCCESS),
  })
  .merge(hostUnassociateUserSuccessPayloadSchema);

const hostUnassociateUserErrorSchema = baseMessageSchema
  .extend({
    type: z.literal(HostMessageType.HOST_UNASSOCIATE_USER_ERROR),
  })
  .merge(hostUnassociateUserErrorPayloadSchema);

export const hostMessageSchema = z.discriminatedUnion('type', [
  hostSettingsSchema,
  hostSettingsSuccessSchema,
  hostSettingsErrorSchema,
  hostSettingsUpdateSchema,
  hostAssociateUserSchema,
  hostAssociateUserSuccessSchema,
  hostAssociateUserErrorSchema,
  hostUnassociateUserSchema,
  hostUnassociateUserSuccessSchema,
  hostUnassociateUserErrorSchema,
]);

// Payload types
export type HostSettingsPayload = z.infer<typeof hostSettingsPayloadSchema>;
export type HostSettingsUpdatePayload = z.infer<typeof hostSettingsUpdatePayloadSchema>;
export type HostSettingsSuccessPayload = z.infer<typeof hostSettingsSuccessPayloadSchema>;
export type HostSettingsErrorPayload = z.infer<typeof hostSettingsErrorPayloadSchema>;
export type HostAssociateUserPayload = z.infer<typeof hostAssociateUserPayloadSchema>;
export type HostAssociateUserSuccessPayload = z.infer<typeof hostAssociateUserSuccessPayloadSchema>;
export type HostAssociateUserErrorPayload = z.infer<typeof hostAssociateUserErrorPayloadSchema>;
export type HostUnassociateUserPayload = z.infer<typeof hostUnassociateUserPayloadSchema>;
export type HostUnassociateUserSuccessPayload = z.infer<typeof hostUnassociateUserSuccessPayloadSchema>;
export type HostUnassociateUserErrorPayload = z.infer<typeof hostUnassociateUserErrorPayloadSchema>;

// Full message type
export type HostMessage = z.infer<typeof hostMessageSchema>;

type PayloadType<T extends HostMessageType> = T extends HostMessageType.HOST_SETTINGS
  ? HostSettingsPayload
  : T extends HostMessageType.HOST_SETTINGS_UPDATE
    ? HostSettingsUpdatePayload
    : T extends HostMessageType.HOST_SETTINGS_SUCCESS
      ? HostSettingsSuccessPayload
      : T extends HostMessageType.HOST_SETTINGS_ERROR
        ? HostSettingsErrorPayload
        : T extends HostMessageType.HOST_ASSOCIATE_USER
          ? HostAssociateUserPayload
          : T extends HostMessageType.HOST_ASSOCIATE_USER_SUCCESS
            ? HostAssociateUserSuccessPayload
            : T extends HostMessageType.HOST_ASSOCIATE_USER_ERROR
              ? HostAssociateUserErrorPayload
              : T extends HostMessageType.HOST_UNASSOCIATE_USER
                ? HostUnassociateUserPayload
                : T extends HostMessageType.HOST_UNASSOCIATE_USER_SUCCESS
                  ? HostUnassociateUserSuccessPayload
                  : T extends HostMessageType.HOST_UNASSOCIATE_USER_ERROR
                    ? HostUnassociateUserErrorPayload
                    : never;

export function generateHostMessage<T extends HostMessageType>(type: T, payload: PayloadType<T>): HostMessage {
  const base = {
    id: crypto.randomUUID(),
    timestamp: Date.now(),
    type,
  };

  switch (type) {
    case HostMessageType.HOST_SETTINGS:
      return hostSettingsSchema.parse({
        ...base,
        ...payload,
      });
    case HostMessageType.HOST_SETTINGS_UPDATE:
      return hostSettingsUpdateSchema.parse({
        ...base,
        ...payload,
      });
    case HostMessageType.HOST_SETTINGS_SUCCESS:
      return hostSettingsSuccessSchema.parse({
        ...base,
        ...payload,
      });
    case HostMessageType.HOST_SETTINGS_ERROR:
      return hostSettingsErrorSchema.parse({
        ...base,
        ...payload,
      });
    case HostMessageType.HOST_ASSOCIATE_USER:
      return hostAssociateUserSchema.parse({
        ...base,
        ...payload,
      });
    case HostMessageType.HOST_ASSOCIATE_USER_SUCCESS:
      return hostAssociateUserSuccessSchema.parse({
        ...base,
        ...payload,
      });
    case HostMessageType.HOST_ASSOCIATE_USER_ERROR:
      return hostAssociateUserErrorSchema.parse({
        ...base,
        ...payload,
      });
    case HostMessageType.HOST_UNASSOCIATE_USER:
      return hostUnassociateUserSchema.parse({
        ...base,
        ...payload,
      });
    case HostMessageType.HOST_UNASSOCIATE_USER_SUCCESS:
      return hostUnassociateUserSuccessSchema.parse({
        ...base,
        ...payload,
      });
    case HostMessageType.HOST_UNASSOCIATE_USER_ERROR:
      return hostUnassociateUserErrorSchema.parse({
        ...base,
        ...payload,
      });
    default: {
      const exhaustiveCheck: never = type;
      throw new Error(`Unhandled message type: ${exhaustiveCheck}`);
    }
  }
}
